import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mutate } from '../shared/api';
import { readUrlParam, useUrlSync } from '../shared/urlState';
import { DateInput } from '../shared/DateInput';

/**
 * Auto-refresh hook: zove callback svake `intervalMs`, ali SAMO ako:
 *  - tab nije skriven (document.hidden)
 *  - aktivni element nije input/select/textarea (user nije usred edita)
 * Tako tuđe promjene stignu bez prekidanja vlastitog inline edita.
 */
function useAutoRefresh(callback: () => void | Promise<void>, intervalMs: number): void {
    const cbRef = useRef(callback);
    useEffect(() => { cbRef.current = callback; }, [callback]);
    useEffect(() => {
        const id = window.setInterval(() => {
            if (document.hidden) return;
            const el = document.activeElement as HTMLElement | null;
            const tag = el?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            void cbRef.current();
        }, intervalMs);
        return () => window.clearInterval(id);
    }, [intervalMs]);
}

/**
 * m.11.1 Team Tasks — sidebar (lista timova) + main (Gantt / Upravljanje / Email log).
 * Layout: lijevo 240px sidebar, desno full-width main panel.
 */

type Locale = 'hr' | 'en';
type Member = { id: number; name: string; email: string; role?: string };
type Team = {
    id: number; name: string; color: string;
    inbound_local_part: string | null;
    inbound_enabled: boolean | number;
    inbound_address: string | null;
    role: string;
    member_count: number;
    project_count: number;
    created_at: string;
};
type InboundLogEntry = {
    id: number; from_addr: string | null; from_name: string | null; to_addr: string | null;
    subject: string | null; parse_mode: string | null; status: string;
    error_message: string | null; email_date: string | null;
    processed_at: string | null; imap_deleted_at: string | null; created_at: string;
    project_name: string | null; task_id_ref: number | null; task_title: string | null;
    assignee_name: string | null;
};
type GanttRow = {
    id: string; kind: 'project' | 'task'; level: number; title: string;
    id_label?: string;
    project_id?: number; task_id?: number;
    assignee_user_name?: string | null; assignee_label?: string | null;
    start_date: string | null; end_date: string | null;
    progress_done?: number; progress_plan?: number; percent?: number;
    priority?: string; status?: string; color?: string; icon?: string;
    predecessor_task_id?: number | null;
    comment_count?: number; nudge_count?: number;
};
type GanttResp = { range: { min: string; max: string }; rows: GanttRow[] };
type Tab = 'all' | 'gantt' | 'log' | 'members';
type AllTasksResp = {
    tasks: Array<{
        id: number; title: string; parent_task_id: number | null;
        assignee_user_id: number | null; assignee_user_name: string | null; assignee_label: string | null;
        start_date: string | null; due_date: string | null; completed_at: string | null;
        priority: string; custom_status: string | null;
        progress_done: number | null; progress_plan: number | null; percent: number;
        is_subtask: boolean;
        deleted_at: string | null;
        project_id: number; project_name: string; project_color: string; project_icon: string;
        comment_count: number; nudge_count: number;
    }>;
    counts: { mine: number; all_open: number; overdue: number; today: number; deleted: number };
};
type TaskComment = {
    id: number; task_id: number;
    body: string; author_user_id: number; author_name: string | null; author_email: string | null;
    source: string; nudge_id: number | null;
    created_at: string;
};
type TaskNudge = {
    id: number; note: string | null; email_status: string; replied_at: string | null; reply_comment_id: number | null;
    created_at: string;
    nudger_name: string | null; nudgee_name: string | null;
};
type TaskDetailResp = {
    task: {
        id: number; title: string; description: string | null;
        project_id: number;
        assignee_user_id: number | null; assignee_name: string | null; assignee_email: string | null;
        creator_name: string | null;
        start_date: string | null; due_date: string | null; completed_at: string | null;
        priority: string; custom_status: string | null;
        progress_done: number | null; progress_plan: number | null;
        parent_task_id: number | null;
    };
    subtasks: Array<{ id: number; title: string; completed_at: string | null; assignee_name: string | null }>;
    comments: TaskComment[];
    followers: unknown[];
};
type TeamProject = {
    id: number; name: string; description: string | null; color: string; icon: string;
    status: string; due_date: string | null;
    task_count: number; completed_count: number;
};
type TeamTask = {
    id: number; project_id: number; parent_task_id: number | null;
    title: string; description: string | null;
    assignee_user_id: number | null; assignee_label: string | null;
    start_date: string | null; due_date: string | null;
    completed_at: string | null;
    priority: string; custom_status: string | null;
    progress_done: number | null; progress_plan: number | null;
    predecessor_task_id: number | null;
    sort_order: number;
};

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

// Redizajn (v355.78): isti jezik dizajna kao Tasks2 (t2-shell) - ink #172033, bijele plohe, pill navigacija.
const ttInk = '#172033';
const ttBtn = (fill = true): React.CSSProperties => ({
    padding: '8px 14px', border: `1px solid ${fill ? ttInk : '#cfd6df'}`, borderRadius: 9,
    background: fill ? ttInk : '#fff', color: fill ? '#fff' : '#28364d',
    cursor: 'pointer', fontWeight: 650, fontSize: 13,
});
const ttPill = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', background: active ? ttInk : 'transparent',
    color: active ? '#fff' : '#536176', border: '1px solid ' + (active ? ttInk : '#cfd6df'),
    borderRadius: 18, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700, whiteSpace: 'nowrap',
});

export function TeamTasksModule({ token, locale, onBack, onOpenAsana, onOpenTasks2, embedded }: { token: string | null; locale: Locale; onBack?: () => void; onOpenAsana?: () => void; onOpenTasks2?: () => void; embedded?: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [teams, setTeams] = useState<Team[]>([]);
    const [allUsers, setAllUsers] = useState<Member[]>([]);
    const [activeTeamId, setActiveTeamId] = useState<number | null>(null);
    const [tab, setTab] = useState<Tab>(readUrlParam('tab', 'all') as Tab);
    useUrlSync('tab', tab, tab === 'all');
    const [showNewTeam, setShowNewTeam] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openTaskId, setOpenTaskId] = useState<number | null>(null);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true); setError('');
        try {
            const [tr, sr] = await Promise.all([
                api<{ teams: Team[] }>('/tasks/teams', token),
                api<{ all_users: Member[] }>('/tasks/state', token),
            ]);
            setTeams(tr.teams);
            setAllUsers(sr.all_users);
            if (!activeTeamId && tr.teams.length > 0) setActiveTeamId(tr.teams[0].id);
        } catch (e) { setError(String(e)); }
        setLoading(false);
    }, [token, activeTeamId]);

    useEffect(() => { void load(); }, [load]);
    useAutoRefresh(load, 60000);

    const activeTeam = teams.find((tm) => tm.id === activeTeamId) ?? null;

    // v347.6 — mobile master-detail: na <720px viewport sidebar i detail se ne pokazuju paralelno.
    // Kad NIJE odabran tim → sidebar full width. Kad JE → detail full width s "← Timovi" gumbom.
    const isMobile = useIsMobile();
    const showSidebar = !isMobile || !activeTeamId;
    const showDetail = !isMobile || !!activeTeamId;

    return (
        <div className="tt-shell" style={embedded
            ? { background: '#f4f6f9', display: 'flex', flexDirection: 'column', minHeight: '100vh', borderRadius: 12, overflow: 'hidden', color: ttInk }
            : { position: 'fixed', inset: 0, zIndex: 4000, background: '#f4f6f9', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: ttInk }
        }>
            <style>{`
                .tt-shell { font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 14px; line-height: 1.45; }
                .tt-shell button, .tt-shell input, .tt-shell select, .tt-shell textarea { font: inherit; }
                .tt-shell button:focus-visible, .tt-shell input:focus-visible, .tt-shell select:focus-visible, .tt-shell textarea:focus-visible { outline: 3px solid rgba(155,107,22,.24); outline-offset: 2px; }
                .tt-shell *::-webkit-scrollbar { height: 9px; width: 9px; }
                .tt-shell *::-webkit-scrollbar-track { background: transparent; }
                .tt-shell *::-webkit-scrollbar-thumb { background: #c9d1dc; border-radius: 8px; }
                .tt-team-btn { transition: background .12s ease, border-color .12s ease; }
                .tt-team-btn:hover { background: #eef1f6 !important; }
                @media (max-width: 720px) {
                    .tt-header { padding: 10px 12px !important; }
                }
            `}</style>

            {/* Topbar - izlaz, naslov, prekidac modula 11.1 / 11.2 / 11.3 */}
            <div className="tt-header" style={{
                background: '#fff', borderBottom: '1px solid #dfe4ea', padding: '12px 20px',
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                boxShadow: '0 2px 10px rgba(23,32,51,.05)',
            }}>
                {onBack ? (
                    <button type="button" style={{ ...ttBtn(false), padding: '6px 12px' }} onClick={onBack}>{t('Izlaz', 'Exit')}</button>
                ) : null}
                <span style={{ fontWeight: 700, fontSize: 17 }}>{t('Zadaci', 'Tasks')}</span>
                <div style={{ flex: 1 }} />
                <button type="button" style={ttPill(true)} title="m.11.1">{t('Timski', 'Teams')}</button>
                {onOpenAsana ? (
                    <button type="button" style={ttPill(false)} title="m.11.2" onClick={onOpenAsana}>{t('Moja Asana', 'My Asana')}</button>
                ) : null}
                {onOpenTasks2 ? (
                    <button type="button" style={ttPill(false)} title="m.11.3" onClick={onOpenTasks2}>{t('Zadaci v2', 'Tasks v2')}</button>
                ) : null}
            </div>

            {/* Error bar */}
            {error ? (
                <div style={{ background: '#fdecea', color: '#b14040', padding: '8px 20px', fontSize: 14, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{error}</span>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b14040', fontWeight: 700 }}
                        onClick={() => setError('')}>x</button>
                </div>
            ) : null}

            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {/* Sidebar - lista timova */}
                {showSidebar ? (
                <aside style={{ width: isMobile ? '100%' : 272, flexShrink: 0, borderRight: isMobile ? 0 : '1px solid #e3e8ee', background: '#fff', overflowY: 'auto', padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                        <strong style={{ fontSize: '0.95rem' }}>{t('Timovi', 'Teams')}</strong>
                        <span style={{ fontSize: 12, color: '#8a95a5' }}>{teams.length}</span>
                    </div>
                    <button type="button" onClick={() => setShowNewTeam(true)} style={{ ...ttBtn(), width: '100%', marginBottom: 10 }}>
                        + {t('Novi tim', 'New team')}
                    </button>
                    {loading && teams.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {[0, 1, 2].map((i) => <div key={i} style={{ height: 58, borderRadius: 10, background: '#eef1f5' }} />)}
                        </div>
                    ) : null}
                    {teams.length === 0 && !loading ? (
                        <div style={{ textAlign: 'center', padding: '28px 10px', color: '#8a95a5' }}>
                            <div style={{ fontSize: 30, marginBottom: 8 }}>👥</div>
                            <div style={{ fontWeight: 600, color: '#536176' }}>{t('Jos nema timova', 'No teams yet')}</div>
                            <div style={{ fontSize: 12, marginTop: 4 }}>{t('Kreiraj prvi tim gumbom iznad.', 'Create the first team with the button above.')}</div>
                        </div>
                    ) : null}
                    {teams.map((team) => (
                        <button key={team.id} type="button" className="tt-team-btn" onClick={() => setActiveTeamId(team.id)}
                            style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                padding: '10px 12px', marginBottom: 6,
                                background: activeTeamId === team.id ? '#eef3fb' : '#fff',
                                border: activeTeamId === team.id ? '1px solid #9fb6d9' : '1px solid #e3e8ee',
                                borderRadius: 10, cursor: 'pointer', color: ttInk,
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: team.color || ttInk, flexShrink: 0 }} />
                                <strong style={{ fontSize: '0.88rem' }}>{team.name}</strong>
                            </div>
                            <div style={{ marginTop: 4, fontSize: '0.74rem', color: '#8a95a5' }}>
                                {team.member_count} {t('članova', 'members')} · {team.project_count} {t('projekata', 'projects')}
                            </div>
                            {team.inbound_address ? (
                                <div style={{ marginTop: 3, fontSize: '0.68rem', color: team.inbound_enabled ? '#1f7a36' : '#8a95a5', fontFamily: 'monospace' }}>
                                    {team.inbound_address}
                                </div>
                            ) : null}
                        </button>
                    ))}
                </aside>
                ) : null}

                {/* Main panel */}
                {showDetail ? (
                <main style={{ flex: 1, overflow: 'hidden', background: '#f4f6f9', display: 'flex', flexDirection: 'column' }}>
                    {isMobile && activeTeamId ? (
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid #e3e8ee', background: '#fff', flexShrink: 0 }}>
                            <button type="button" onClick={() => setActiveTeamId(null)} style={{ ...ttBtn(false), padding: '5px 10px' }}>
                                ← {t('Timovi', 'Teams')}
                            </button>
                        </div>
                    ) : null}
                    {!activeTeam ? (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, padding: 40, color: '#8a95a5' }}>
                            <div style={{ fontSize: 34 }}>🗂️</div>
                            <div style={{ fontWeight: 600, color: '#536176' }}>
                                {teams.length === 0
                                    ? t('Kreiraj prvi tim u stupcu lijevo.', 'Create your first team in the left column.')
                                    : t('Odaberi tim iz stupca lijevo.', 'Select a team from the left column.')}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Header tima */}
                            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e3e8ee', background: '#fff', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
                                <span style={{ width: 12, height: 12, borderRadius: 4, background: activeTeam.color || ttInk }} />
                                <h2 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 700, color: ttInk }}>{activeTeam.name}</h2>
                                {activeTeam.inbound_address ? (
                                    <code style={{ background: '#eef1f5', padding: '3px 9px', borderRadius: 8, fontSize: '0.76rem', color: '#536176' }}>
                                        {activeTeam.inbound_address}
                                    </code>
                                ) : null}
                                <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 9px', background: '#eef1f5', color: '#536176', borderRadius: 10 }}>
                                    {activeTeam.role}
                                </span>
                                <span style={{ fontSize: '0.76rem', color: '#8a95a5' }}>
                                    {activeTeam.member_count} {t('članova', 'members')}
                                </span>
                                <span style={{ marginLeft: 'auto' }} />
                                <TeamSettingsButton team={activeTeam} token={token} locale={locale} onChanged={() => void load()} />
                            </div>

                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: 6, padding: '12px 20px', borderBottom: '1px solid #e3e8ee', background: '#fff', overflowX: 'auto', flexShrink: 0 }}>
                                {([
                                    ['all', t('Svi zadaci', 'All tasks')],
                                    ['gantt', t('Gantt + projekti', 'Gantt + projects')],
                                    ['members', t('Članovi', 'Members')],
                                    ['log', t('Email log', 'Email log')],
                                ] as [Tab, string][]).map(([k, label]) => (
                                    <button key={k} type="button" onClick={() => setTab(k)} style={ttPill(tab === k)}>{label}</button>
                                ))}
                            </div>

                            {/* Content */}
                            <div style={{ padding: isMobile ? 12 : 20, flex: 1, overflow: 'auto' }}>
                                {tab === 'all' ? <AllTasksTab teamId={activeTeam.id} token={token} locale={locale} onChanged={() => void load()} onOpenTask={setOpenTaskId} /> : null}
                                {tab === 'gantt' ? <GanttTab teamId={activeTeam.id} token={token} locale={locale} onChanged={() => void load()} onOpenTask={setOpenTaskId} /> : null}
                                {tab === 'members' ? <MembersTab team={activeTeam} allUsers={allUsers} token={token} locale={locale} onChanged={() => void load()} /> : null}
                                {tab === 'log' ? <LogTab teamId={activeTeam.id} token={token} locale={locale} /> : null}
                            </div>
                        </>
                    )}
                </main>
                ) : null}
            </div>

            {showNewTeam ? <NewTeamModal allUsers={allUsers} token={token} locale={locale} onClose={() => setShowNewTeam(false)} onCreated={() => { setShowNewTeam(false); void load(); }} /> : null}
            {openTaskId !== null ? <TaskDrawer taskId={openTaskId} token={token} locale={locale} onClose={() => setOpenTaskId(null)} onChanged={() => { void load(); }} /> : null}
        </div>
    );
}

function TeamSettingsButton({ team, token, locale, onChanged }: { team: Team; token: string | null; locale: Locale; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [open, setOpen] = useState(false);
    const [alias, setAlias] = useState(team.inbound_local_part ?? '');
    const [busy, setBusy] = useState(false);
    useEffect(() => { setAlias(team.inbound_local_part ?? ''); }, [team.id, team.inbound_local_part]);

    const saveAlias = async () => {
        if (!token) return;
        const slug = alias.trim().toLowerCase();
        setBusy(true);
        try {
            await mutate(`/tasks/teams/${team.id}`, token, 'PATCH', { inbound_local_part: slug || null, inbound_enabled: !!slug });
            onChanged();
        } catch (e) { alert(String(e)); }
        setBusy(false);
    };
    const toggleInbound = async () => {
        if (!token || !team.inbound_local_part) return;
        setBusy(true);
        try {
            await mutate(`/tasks/teams/${team.id}`, token, 'PATCH', { inbound_enabled: !team.inbound_enabled });
            onChanged();
        } catch (e) { alert(String(e)); }
        setBusy(false);
    };

    return (
        <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(!open)} style={ttBtn(false)}>
                {t('Postavke', 'Settings')}
            </button>
            {open ? (
                <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={() => setOpen(false)} />
                    <div style={{
                        position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 61,
                        width: 'min(320px, 86vw)', background: '#fff', border: '1px solid #e3e8ee',
                        borderRadius: 12, boxShadow: '0 12px 32px rgba(23,32,51,.14)', padding: 16,
                    }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: ttInk }}>
                            {t('Postavke tima', 'Team settings')}
                        </div>
                        <label style={{ fontSize: 12, color: '#536176', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                            {t('Email alias (dolazna posta)', 'Email alias (inbound mail)')}
                        </label>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                            <input value={alias} onChange={(e) => setAlias(e.target.value)}
                                placeholder={t('npr. prodaja', 'e.g. sales')}
                                style={{ flex: 1, padding: '8px 10px', border: '1px solid #cfd6df', borderRadius: 8, fontSize: 13, minWidth: 0 }} />
                            <button type="button" disabled={busy} onClick={() => void saveAlias()} style={{ ...ttBtn(), padding: '7px 12px' }}>
                                {t('Spremi', 'Save')}
                            </button>
                        </div>
                        <p style={{ fontSize: 11, color: '#8a95a5', margin: '0 0 10px' }}>
                            {t('Mail na todo.{alias}@aurodomus.eu postaje zadatak tima.', 'Mail to todo.{alias}@aurodomus.eu becomes a team task.')}
                        </p>
                        {team.inbound_local_part ? (
                            <button type="button" disabled={busy} onClick={() => void toggleInbound()}
                                style={{
                                    width: '100%', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    background: team.inbound_enabled ? 'rgba(31,122,54,0.10)' : '#fff',
                                    color: team.inbound_enabled ? '#1f7a36' : '#8a95a5',
                                    border: `1px solid ${team.inbound_enabled ? '#1f7a36' : '#cfd6df'}`,
                                }}>
                                {team.inbound_enabled ? t('Intake aktivan - klikni za iskljucenje', 'Intake active - click to disable') : t('Intake iskljucen - klikni za ukljucenje', 'Intake off - click to enable')}
                            </button>
                        ) : null}
                    </div>
                </>
            ) : null}
        </div>
    );
}

type ScopeFilter = 'all' | 'mine' | 'unassigned';
type StatusFilter = 'open' | 'overdue' | 'today' | 'completed' | 'deleted' | 'all';

function AllTasksTab({ teamId, token, locale, onChanged, onOpenTask }: { teamId: number; token: string | null; locale: Locale; onChanged: () => void; onOpenTask: (id: number) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<AllTasksResp | null>(null);
    const [members, setMembers] = useState<Member[]>([]);
    const [scope, setScope] = useState<ScopeFilter>('all');
    const [statusF, setStatusF] = useState<StatusFilter>('open');
    const [search, setSearch] = useState('');
    const [busy, setBusy] = useState(false);
    const [nudgeTaskId, setNudgeTaskId] = useState<number | null>(null);

    const load = useCallback(async () => {
        if (!token) return;
        setBusy(true);
        try {
            const [r, m] = await Promise.all([
                api<AllTasksResp>(`/tasks/teams/${teamId}/all-tasks?scope=${scope}&status=${statusF}`, token),
                api<{ members: Member[] }>(`/tasks/teams/${teamId}/members-list`, token),
            ]);
            setData(r); setMembers(m.members);
        } catch (e) { /* */ }
        setBusy(false);
    }, [token, teamId, scope, statusF]);

    useEffect(() => { void load(); }, [load]);
    useAutoRefresh(load, 30000);

    const patchTask = async (taskId: number, patch: Record<string, unknown>) => {
        if (!token) return;
        try {
            await mutate(`/tasks/tasks/${taskId}`, token, 'PATCH', patch);
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };
    const toggleComplete = async (taskId: number, currently: boolean) => {
        if (!token) return;
        try {
            await mutate(`/tasks/tasks/${taskId}/complete`, token, 'POST');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        void currently;
    };

    const restoreTask = async (taskId: number) => {
        if (!token) return;
        try {
            await mutate(`/tasks/tasks/${taskId}/restore`, token, 'POST');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    const deleteTask = async (taskId: number) => {
        if (!token) return;
        if (!confirm(t('Obrisati task? Možeš ga vratiti pod 🗑 Obrisani.', 'Delete task? You can restore it under 🗑 Deleted.'))) return;
        try {
            await mutate(`/tasks/tasks/${taskId}`, token, 'DELETE');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    const rows = (data?.tasks ?? []).filter((row) => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        return row.title.toLowerCase().includes(s)
            || row.project_name.toLowerCase().includes(s)
            || (row.assignee_user_name ?? '').toLowerCase().includes(s);
    });

    const fmtDate = (s: string | null) => {
        if (!s) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
        return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}.` : '';
    };
    const isOverdue = (due: string | null, completed: string | null) => {
        if (!due || completed) return false;
        return due < new Date().toISOString().slice(0, 10);
    };

    return (
        <div>
            {/* Filter chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center' }}>
                <strong style={{ color: BROWN, fontSize: '0.84rem', marginRight: 6 }}>{t('Tko', 'Who')}:</strong>
                <ChipBtn active={scope === 'all'} onClick={() => setScope('all')}>{t('Svi', 'All')} <span style={{ opacity: 0.7 }}>({data?.counts.all_open ?? 0})</span></ChipBtn>
                <ChipBtn active={scope === 'mine'} onClick={() => setScope('mine')}>{t('Moji', 'Mine')} <span style={{ opacity: 0.7 }}>({data?.counts.mine ?? 0})</span></ChipBtn>
                <ChipBtn active={scope === 'unassigned'} onClick={() => setScope('unassigned')}>{t('Nedodjeljeni', 'Unassigned')}</ChipBtn>
                <span style={{ width: 1, height: 20, background: 'rgba(var(--ad-soft-rgb),0.4)', margin: '0 8px' }} />
                <strong style={{ color: BROWN, fontSize: '0.84rem', marginRight: 6 }}>{t('Status', 'Status')}:</strong>
                <ChipBtn active={statusF === 'open'} onClick={() => setStatusF('open')}>{t('Otvoreni', 'Open')}</ChipBtn>
                <ChipBtn active={statusF === 'today'} onClick={() => setStatusF('today')}>{t('Danas', 'Today')} <span style={{ opacity: 0.7 }}>({data?.counts.today ?? 0})</span></ChipBtn>
                <ChipBtn active={statusF === 'overdue'} onClick={() => setStatusF('overdue')} danger>{t('Probijeni', 'Overdue')} <span style={{ opacity: 0.7 }}>({data?.counts.overdue ?? 0})</span></ChipBtn>
                <ChipBtn active={statusF === 'completed'} onClick={() => setStatusF('completed')}>{t('Završeni', 'Completed')}</ChipBtn>
                <ChipBtn active={statusF === 'deleted'} onClick={() => setStatusF('deleted')}>🗑 {t('Obrisani', 'Deleted')} <span style={{ opacity: 0.7 }}>({data?.counts.deleted ?? 0})</span></ChipBtn>
                <ChipBtn active={statusF === 'all'} onClick={() => setStatusF('all')}>{t('Svi', 'All')}</ChipBtn>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('🔍 Pretraži…', '🔍 Search…')}
                    style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 14, border: '1px solid var(--ad-acc)', fontSize: '0.8rem', width: 180 }} />
            </div>

            {busy ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {rows.length === 0 && !busy ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#888', border: '1px dashed #ccc', borderRadius: 8 }}>
                    {t('Nema zadataka po ovom filtru.', 'No tasks under this filter.')}
                </div>
            ) : null}

            <div style={{ background: '#fff', borderRadius: 6, overflowX: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.2)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                        <tr style={{ background: 'var(--ad-bg)', color: BROWN, fontSize: '0.72rem' }}>
                            <th style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '1px solid #eee', width: 30 }}>✓</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>TITLE</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee', width: 160 }}>PROJECT</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee', width: 150 }}>ASSIGNED</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee', width: 130 }}>STATUS</th>
                            <th style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '1px solid #eee', width: 80 }}>DUE</th>
                            <th style={{ padding: '8px 4px', textAlign: 'right', borderBottom: '1px solid #eee', width: 70 }}>%</th>
                            <th style={{ padding: '8px 4px', textAlign: 'center', borderBottom: '1px solid #eee', width: 60 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const overdue = isOverdue(row.due_date, row.completed_at);
                            return (
                                <tr key={row.id} style={{ borderBottom: '1px solid #f6f6f6', background: row.deleted_at ? 'rgba(120,120,120,0.05)' : row.completed_at ? 'rgba(31,122,54,0.04)' : (overdue ? 'rgba(177,64,64,0.06)' : 'transparent'), opacity: row.deleted_at ? 0.55 : 1 }}>
                                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                                        {row.deleted_at
                                            ? <span title={t('Obrisan', 'Deleted')} style={{ color: '#888', fontSize: '0.9rem' }}>🗑</span>
                                            : <input type="checkbox" checked={!!row.completed_at} onChange={() => void toggleComplete(row.id, !!row.completed_at)} />}
                                    </td>
                                    <td style={{ padding: '5px 10px' }}>
                                        {row.is_subtask ? <span style={{ color: '#888', marginRight: 4 }}>↳</span> : null}
                                        <input type="text" defaultValue={row.title} title={row.title}
                                            onBlur={(e) => { if (e.target.value.trim() !== row.title) void patchTask(row.id, { title: e.target.value.trim() }); }}
                                            style={{ border: 0, background: 'transparent', fontSize: '0.84rem', width: 'calc(100% - 90px)', textDecoration: row.completed_at ? 'line-through' : 'none', color: row.completed_at ? '#888' : '#333', padding: 2 }} />
                                        <button type="button" onClick={() => onOpenTask(row.id)} title={t('Otvori detalje', 'Open details')}
                                            style={{ background: 'transparent', border: '1px solid var(--ad-acc)', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontSize: '0.7rem', color: BROWN, marginLeft: 4 }}>
                                            💬 {row.comment_count > 0 ? row.comment_count : ''}{row.nudge_count > 0 ? ` 🫵${row.nudge_count}` : ''}
                                        </button>
                                    </td>
                                    <td style={{ padding: '5px 10px', fontSize: '0.78rem' }}>
                                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: row.project_color, marginRight: 4 }} />
                                        {row.project_icon} {row.project_name}
                                    </td>
                                    <td style={{ padding: '5px 10px' }}>
                                        <select value={row.assignee_user_id ?? ''} onChange={(e) => void patchTask(row.id, { assignee_user_id: e.target.value ? Number(e.target.value) : null })}
                                            style={{ width: '100%', border: 0, background: 'transparent', fontSize: '0.76rem', color: row.assignee_user_id ? '#444' : '#aaa' }}>
                                            <option value="">{t('— bez —', '— none —')}</option>
                                            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                                        </select>
                                    </td>
                                    <td style={{ padding: '5px 10px' }}>
                                        <select value={row.custom_status ?? ''} onChange={(e) => void patchTask(row.id, { custom_status: e.target.value || null })}
                                            style={{ width: '100%', border: 0, background: 'transparent', fontSize: '0.72rem', color: statusTextColor(row.custom_status), fontWeight: 700 }}>
                                            <option value="">—</option>
                                            <option value="not_started">NOT STARTED</option>
                                            <option value="in_progress">IN PROGRESS</option>
                                            <option value="on_hold">ON HOLD</option>
                                            <option value="completed">COMPLETED</option>
                                        </select>
                                    </td>
                                    <td style={{ padding: '5px 4px', textAlign: 'center', color: overdue ? '#b14040' : (row.completed_at ? '#888' : '#444'), fontWeight: overdue ? 700 : 400 }}>
                                        <DateCell iso={row.due_date} onChange={(iso) => void patchTask(row.id, { due_date: iso })} width={70} />
                                    </td>
                                    <td style={{ padding: '5px 4px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: row.percent >= 100 ? '#1f7a36' : row.percent > 0 ? 'var(--ad-acc)' : '#bbb' }}>
                                        {row.percent > 0 ? `${row.percent}%` : ''}
                                    </td>
                                    <td style={{ padding: '5px 4px', textAlign: 'center', display: 'flex', gap: 3, justifyContent: 'center' }}>
                                        {row.deleted_at ? (
                                            <button type="button" onClick={() => void restoreTask(row.id)} title={t('Vrati', 'Restore')}
                                                style={{ background: 'transparent', border: '1px solid #1f7a36', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: '0.72rem', color: '#1f7a36', fontWeight: 700 }}>↶ {t('Vrati', 'Restore')}</button>
                                        ) : (
                                            <>
                                                {row.assignee_user_id && !row.completed_at ? (
                                                    <button type="button" onClick={() => setNudgeTaskId(row.id)} title={t('Nudge — pošalji email nositelju', 'Nudge — email assignee')}
                                                        style={{ background: 'transparent', border: '1px solid var(--ad-acc)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: '0.74rem', color: BROWN }}>🫵</button>
                                                ) : null}
                                                <button type="button" onClick={() => void deleteTask(row.id)} title={t('Obriši', 'Delete')}
                                                    style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: '0.72rem', color: '#b14040' }}>🗑</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 6, fontSize: '0.74rem', color: '#888' }}>
                {rows.length} {t('redaka prikazano', 'rows shown')}
            </div>

            {nudgeTaskId !== null ? (
                <NudgeModal taskId={nudgeTaskId} taskTitle={rows.find((r) => r.id === nudgeTaskId)?.title ?? ''}
                    assigneeName={rows.find((r) => r.id === nudgeTaskId)?.assignee_user_name ?? null}
                    token={token} locale={locale}
                    onClose={() => setNudgeTaskId(null)}
                    onSent={() => { setNudgeTaskId(null); onChanged(); }} />
            ) : null}
        </div>
    );
}

function NudgeModal({ taskId, taskTitle, assigneeName, token, locale, onClose, onSent }: { taskId: number; taskTitle: string; assigneeName: string | null; token: string | null; locale: Locale; onClose: () => void; onSent: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const send = async () => {
        if (!token) return;
        setBusy(true);
        try {
            const r = await mutate<{ ok: boolean; error?: string }>(`/tasks/tasks/${taskId}/nudge`, token, 'POST', { note: note.trim() || null });
            if (r.ok) {
                alert(t('🫵 Nudge poslan!', '🫵 Nudge sent!'));
                onSent();
            } else {
                alert(r.error ?? t('Greška', 'Error'));
            }
        } catch (e) { alert(String(e)); }
        setBusy(false);
    };
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 22, width: 'min(500px, 92vw)' }}>
                <h3 style={{ marginTop: 0, color: BROWN }}>🫵 {t('Nudge', 'Nudge')}</h3>
                <p style={{ fontSize: '0.86rem', color: '#444', marginBottom: 6 }}>
                    {t('Šaljemo email i in-app PING nositelju:', 'Sending email and in-app PING to assignee:')}
                </p>
                <div style={{ padding: 10, background: 'var(--ad-bg)', borderRadius: 6, marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, color: BROWN, fontSize: '0.9rem' }}>{taskTitle}</div>
                    <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 3 }}>{t('Nositelj', 'Assignee')}: <strong>{assigneeName ?? '—'}</strong></div>
                </div>
                <label style={{ display: 'block' }}>
                    <span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Napomena (opcionalno)', 'Note (optional)')}</span>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={2000} autoFocus
                        placeholder={t('npr. „Možeš li ovo do petka?"', 'e.g. "Can you finish this by Friday?"')}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 4, fontSize: '0.88rem', resize: 'vertical', fontFamily: 'inherit' }} />
                </label>
                <p style={{ fontSize: '0.74rem', color: '#888', marginTop: 8, lineHeight: 1.5 }}>
                    {t('💬 Nositelj može odgovoriti direktno na mail — odgovor će se pojaviti kao komentar na task i biti proslijeđen tebi.', '💬 Assignee can reply to the email — reply becomes a task comment and forwards to you.')}
                </p>
                <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} style={{ padding: '7px 16px', background: 'transparent', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={() => void send()} style={{ padding: '7px 18px', background: 'var(--ad-acc)', color: '#fff', border: 0, borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>{busy ? '…' : '🫵 ' + t('Pošalji nudge', 'Send nudge')}</button>
                </div>
            </div>
        </div>
    );
}

function ChipBtn({ active, onClick, danger, children }: { active: boolean; onClick: () => void; danger?: boolean; children: React.ReactNode }) {
    const color = danger ? '#b14040' : 'var(--ad-acc)';
    return (
        <button type="button" onClick={onClick} style={{
            padding: '4px 12px',
            background: active ? (danger ? '#b14040' : GOLD) : 'transparent',
            color: active ? '#fff' : (danger ? '#b14040' : BROWN),
            border: `1px solid ${color}`,
            borderRadius: 14, cursor: 'pointer',
            fontSize: '0.78rem', fontWeight: 600,
        }}>{children}</button>
    );
}

function GanttTab({ teamId, token, locale, onChanged, onOpenTask }: { teamId: number; token: string | null; locale: Locale; onChanged: () => void; onOpenTask: (id: number) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<GanttResp | null>(null);
    const [projects, setProjects] = useState<TeamProject[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [busy, setBusy] = useState(false);
    const [showNewProject, setShowNewProject] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [showNewTask, setShowNewTask] = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        setBusy(true);
        try {
            const [g, m, p] = await Promise.all([
                api<GanttResp>(`/tasks/teams/${teamId}/gantt`, token),
                api<{ members: Member[] }>(`/tasks/teams/${teamId}/members-list`, token),
                api<{ projects: TeamProject[] }>(`/tasks/projects?workspace_id=${teamId}`, token),
            ]);
            setData(g);
            setMembers(m.members);
            setProjects(p.projects);
        } catch (e) { /* */ }
        setBusy(false);
    }, [token, teamId]);

    useEffect(() => { void load(); }, [load]);
    useAutoRefresh(load, 30000);

    const patchTask = async (taskId: number, patch: Partial<TeamTask>) => {
        if (!token) return;
        try {
            await mutate(`/tasks/tasks/${taskId}`, token, 'PATCH', patch as Record<string, unknown>);
            await load();
        } catch (e) { alert(String(e)); }
    };

    const createProject = async () => {
        if (!token || !newProjectName.trim()) return;
        try {
            await mutate('/tasks/projects', token, 'POST', { workspace_id: teamId, name: newProjectName.trim() });
            setShowNewProject(false); setNewProjectName('');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    return (
        <div>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setShowNewProject(true)}
                    style={{ padding: '5px 14px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700 }}>
                    + {t('Novi projekt', 'New project')}
                </button>
                <button type="button" onClick={() => setShowNewTask(true)} disabled={projects.length === 0}
                    style={{ padding: '5px 14px', background: projects.length === 0 ? '#ccc' : '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: projects.length === 0 ? 'not-allowed' : 'pointer', fontSize: '0.84rem', fontWeight: 700 }}>
                    + {t('Novi zadatak', 'New task')}
                </button>
                <span style={{ marginLeft: 8, fontSize: '0.74rem', color: '#888' }}>
                    {projects.length} {t('projekata', 'projects')} · {data?.rows.filter((r) => r.kind === 'task').length ?? 0} {t('zadataka', 'tasks')}
                </span>
            </div>

            {showNewProject ? (
                <div style={{ marginBottom: 10, padding: 10, background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 6 }}>
                    <input type="text" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder={t('Naziv projekta', 'Project name')} autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') void createProject(); if (e.key === 'Escape') setShowNewProject(false); }}
                        style={{ width: '50%', padding: '6px 10px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.86rem' }} />
                    <button type="button" onClick={() => void createProject()} style={{ marginLeft: 8, padding: '6px 14px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>{t('Kreiraj', 'Create')}</button>
                    <button type="button" onClick={() => setShowNewProject(false)} style={{ marginLeft: 4, padding: '6px 12px', background: 'transparent', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem' }}>{t('Odustani', 'Cancel')}</button>
                </div>
            ) : null}

            {showNewTask ? (
                <NewTaskInGanttModal projects={projects} members={members} token={token} locale={locale}
                    onClose={() => setShowNewTask(false)}
                    onCreated={() => { setShowNewTask(false); void load(); onChanged(); }} />
            ) : null}

            {busy ? <p className="muted">{t('Učitavam Gantt…', 'Loading Gantt…')}</p>
                : !data || data.rows.length === 0
                    ? <p className="muted">{t('Nema projekata ni zadataka. Klikni „+ Novi projekt".', 'No projects or tasks. Click "+ New project".')}</p>
                    : <GanttChart data={data} members={members} locale={locale} onPatch={(id, p) => void patchTask(id, p)} onOpenTask={onOpenTask} />
            }
        </div>
    );
}

function NewTaskInGanttModal({ projects, members, token, locale, onClose, onCreated }: { projects: TeamProject[]; members: Member[]; token: string | null; locale: Locale; onClose: () => void; onCreated: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [projectId, setProjectId] = useState<number | ''>(projects[0]?.id ?? '');
    const [title, setTitle] = useState('');
    const [assigneeId, setAssigneeId] = useState<number | ''>('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [plan, setPlan] = useState('');
    const [busy, setBusy] = useState(false);

    const create = async () => {
        if (!token) return;
        if (!projectId) { alert(t('Odaberi projekt', 'Pick a project')); return; }
        if (!title.trim()) { alert(t('Naslov je obavezan', 'Title is required')); return; }
        setBusy(true);
        try {
            await mutate('/tasks/tasks', token, 'POST', {
                project_id: projectId,
                title: title.trim(),
                assignee_user_id: assigneeId || null,
                start_date: start || null,
                due_date: end || null,
                progress_plan: plan ? Number(plan) : null,
            });
            onCreated();
        } catch (e) { alert(String(e)); }
        setBusy(false);
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 22, width: 'min(540px, 92vw)' }}>
                <h3 style={{ marginTop: 0, color: BROWN }}>{t('Novi zadatak', 'New task')}</h3>
                <label style={{ display: 'block', marginTop: 8 }}><span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Projekt', 'Project')}</span>
                    <select value={projectId} onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : '')}
                        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.92rem' }}>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
                    </select>
                </label>
                <label style={{ display: 'block', marginTop: 10 }}><span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Naslov', 'Title')}</span>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
                        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.92rem' }} />
                </label>
                <label style={{ display: 'block', marginTop: 10 }}><span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Dodjela', 'Assignee')}</span>
                    <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value ? Number(e.target.value) : '')}
                        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.92rem' }}>
                        <option value="">{t('— bez dodjele —', '— unassigned —')}</option>
                        {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                </label>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <label style={{ flex: 1 }}><span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Start', 'Start')}</span>
                        <div style={{ marginTop: 3 }}>
                            <DateInput value={start} onChange={setStart}
                                style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', fontSize: '0.86rem' }} />
                        </div>
                    </label>
                    <label style={{ flex: 1 }}><span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('End', 'End')}</span>
                        <div style={{ marginTop: 3 }}>
                            <DateInput value={end} onChange={setEnd}
                                style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', fontSize: '0.86rem' }} />
                        </div>
                    </label>
                    <label style={{ width: 100 }}><span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>Plan</span>
                        <input type="number" min={0} value={plan} onChange={(e) => setPlan(e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.86rem' }} />
                    </label>
                </div>
                <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} style={{ padding: '7px 16px', background: 'transparent', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={() => void create()} style={{ padding: '7px 16px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>{busy ? '…' : t('Kreiraj', 'Create')}</button>
                </div>
            </div>
        </div>
    );
}

function LogTab({ teamId, token, locale }: { teamId: number; token: string | null; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [log, setLog] = useState<InboundLogEntry[]>([]);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        if (!token) return;
        try { const r = await api<{ entries: InboundLogEntry[] }>(`/tasks/teams/${teamId}/inbound-log`, token); setLog(r.entries); } catch (e) { /* */ }
    }, [token, teamId]);
    useEffect(() => {
        setBusy(true);
        void load().finally(() => setBusy(false));
    }, [load]);
    useAutoRefresh(load, 60000);
    if (busy) return <p className="muted">…</p>;
    if (log.length === 0) return <p className="muted">{t('Još nema dolaznih mailova', 'No inbound mails yet')}</p>;
    return (
        <div>
            {log.map((row) => (
                <div key={row.id} style={{ marginBottom: 8, padding: 10, background: 'var(--ad-bg)', borderRadius: 6, fontSize: '0.82rem', borderLeft: `3px solid ${row.status === 'processed' ? '#1f7a36' : row.status === 'error' ? '#b14040' : '#888'}` }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <strong>{row.from_name || row.from_addr}</strong>
                        <span style={{ color: '#888' }}>→ {row.to_addr}</span>
                        <span style={{ marginLeft: 'auto', color: '#888', fontSize: '0.74rem' }}>{row.email_date ?? row.created_at}</span>
                    </div>
                    <div style={{ marginTop: 3 }}>{row.subject}</div>
                    <div style={{ marginTop: 3, color: '#444', fontSize: '0.74rem' }}>
                        {row.status} · {row.parse_mode ?? '-'}
                        {row.project_name ? <> · 📋 {row.project_name}</> : null}
                        {row.assignee_name ? <> · 👤 {row.assignee_name}</> : null}
                        {row.task_id_ref ? <> · <span style={{ color: '#1e40af' }}>#{row.task_id_ref} {row.task_title}</span></> : null}
                    </div>
                    {row.error_message ? <div style={{ marginTop: 3, color: '#b14040', fontSize: '0.74rem' }}>⚠ {row.error_message}</div> : null}
                </div>
            ))}
        </div>
    );
}

function MembersTab({ team, allUsers, token, locale, onChanged }: { team: Team; allUsers: Member[]; token: string | null; locale: Locale; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [members, setMembers] = useState<Member[]>([]);
    const [busy, setBusy] = useState(false);
    const [adding, setAdding] = useState(false);
    const [picker, setPicker] = useState('');

    const load = useCallback(async () => {
        if (!token) return;
        setBusy(true);
        try {
            const r = await api<{ members: Member[] }>(`/tasks/teams/${team.id}/members-list`, token);
            setMembers(r.members);
        } catch (e) { /* */ }
        setBusy(false);
    }, [token, team.id]);

    useEffect(() => { void load(); }, [load]);

    const isAdmin = team.role === 'owner' || team.role === 'admin';
    const candidates = allUsers.filter((u) => !members.find((m) => m.id === u.id) && (!picker || (u.name || '').toLowerCase().includes(picker.toLowerCase()) || (u.email || '').toLowerCase().includes(picker.toLowerCase())));

    const addMember = async (uid: number) => {
        if (!token) return;
        try { await mutate(`/tasks/teams/${team.id}/members`, token, 'POST', { user_id: uid, role: 'member' }); await load(); onChanged(); } catch (e) { alert(String(e)); }
    };
    const removeMember = async (uid: number) => {
        if (!token) return;
        if (!confirm(t('Ukloniti člana?', 'Remove member?'))) return;
        try { await mutate(`/tasks/teams/${team.id}/members/${uid}`, token, 'DELETE'); await load(); onChanged(); } catch (e) { alert(String(e)); }
    };

    return (
        <div style={{ maxWidth: 700 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <strong style={{ color: BROWN }}>{t('Članovi tima', 'Team members')} ({members.length})</strong>
                {isAdmin ? (
                    <button type="button" onClick={() => setAdding(!adding)}
                        style={{ marginLeft: 'auto', padding: '5px 12px', background: adding ? GOLD : '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
                        {adding ? t('Zatvori', 'Close') : '+ ' + t('Dodaj člana', 'Add member')}
                    </button>
                ) : null}
            </div>

            {adding ? (
                <div style={{ marginBottom: 12, padding: 10, background: 'var(--ad-bg)', borderRadius: 6 }}>
                    <input type="text" value={picker} onChange={(e) => setPicker(e.target.value)}
                        placeholder={t('Pretraži po imenu ili mailu…', 'Search by name or email…')} autoFocus
                        style={{ width: '100%', padding: '5px 10px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.84rem' }} />
                    <div style={{ maxHeight: 250, overflowY: 'auto', marginTop: 8 }}>
                        {candidates.slice(0, 30).map((u) => (
                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: '#fff', marginBottom: 3, borderRadius: 4 }}>
                                <span style={{ flex: 1 }}>{u.name}</span>
                                <span style={{ color: '#888', fontSize: '0.74rem' }}>{u.email}</span>
                                <button type="button" onClick={() => void addMember(u.id)}
                                    style={{ padding: '3px 10px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer', fontSize: '0.74rem' }}>+ {t('dodaj', 'add')}</button>
                            </div>
                        ))}
                        {candidates.length === 0 ? <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Nema kandidata', 'No candidates')}</p> : null}
                    </div>
                </div>
            ) : null}

            {busy ? <p className="muted">…</p> : null}
            <div style={{ background: 'var(--ad-bg)', borderRadius: 6, padding: 8 }}>
                {members.map((m) => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.15)' }}>
                        <span style={{ fontWeight: 600, color: BROWN }}>{m.name}</span>
                        <span style={{ color: '#888', fontSize: '0.78rem' }}>{m.email}</span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.74rem', color: m.role === 'owner' ? '#1f7a36' : '#888', fontWeight: 700 }}>{m.role}</span>
                        {isAdmin && m.role !== 'owner' ? (
                            <button type="button" onClick={() => void removeMember(m.id)}
                                style={{ background: 'transparent', border: 0, color: '#b14040', cursor: 'pointer', fontSize: '1rem' }} title={t('Ukloni', 'Remove')}>✕</button>
                        ) : null}
                    </div>
                ))}
            </div>
        </div>
    );
}

function NewTeamModal({ allUsers, token, locale, onClose, onCreated }: { allUsers: Member[]; token: string | null; locale: Locale; onClose: () => void; onCreated: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [name, setName] = useState('');
    const [alias, setAlias] = useState('');
    const [memberIds, setMemberIds] = useState<number[]>([]);
    const [busy, setBusy] = useState(false);

    const create = async () => {
        if (!token) return;
        if (!name.trim()) { alert(t('Naziv je obavezan', 'Name is required')); return; }
        setBusy(true);
        try {
            await mutate('/tasks/teams', token, 'POST', {
                name: name.trim(),
                inbound_local_part: alias.trim() ? alias.trim().toLowerCase() : null,
                inbound_enabled: !!alias.trim(),
                member_user_ids: memberIds,
            });
            onCreated();
        } catch (e) { alert(String(e)); }
        setBusy(false);
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 22, width: 'min(560px, 92vw)', maxHeight: '85vh', overflowY: 'auto' }}>
                <h3 style={{ marginTop: 0, color: BROWN }}>{t('Novi tim', 'New team')}</h3>
                <label style={{ display: 'block', marginTop: 10 }}><span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Naziv tima', 'Team name')}</span>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={191} autoFocus
                        style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--ad-acc)', marginTop: 3, fontSize: '0.92rem' }} />
                </label>
                <label style={{ display: 'block', marginTop: 12 }}>
                    <span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Email alias (opcionalno)', 'Email alias (optional)')}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 3, border: '1px solid var(--ad-acc)', borderRadius: 6, overflow: 'hidden' }}>
                        <span style={{ padding: '7px 8px', background: 'var(--ad-bg)', color: '#888', fontSize: '0.84rem', fontFamily: 'monospace' }}>todo.</span>
                        <input type="text" value={alias} onChange={(e) => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} maxLength={32}
                            placeholder="marketing"
                            style={{ flex: 1, padding: '7px 6px', border: 0, fontSize: '0.92rem', fontFamily: 'monospace' }} />
                        <span style={{ padding: '7px 8px', background: 'var(--ad-bg)', color: '#888', fontSize: '0.84rem', fontFamily: 'monospace' }}>@aurodomus.eu</span>
                    </div>
                </label>
                <div style={{ marginTop: 16 }}>
                    <span style={{ fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>{t('Članovi', 'Members')} ({memberIds.length})</span>
                    <div style={{ marginTop: 6, maxHeight: 280, overflowY: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 6, padding: 6 }}>
                        {allUsers.map((u) => (
                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', fontSize: '0.84rem', cursor: 'pointer' }}>
                                <input type="checkbox" checked={memberIds.includes(u.id)}
                                    onChange={(e) => setMemberIds(e.target.checked ? [...memberIds, u.id] : memberIds.filter((i) => i !== u.id))} />
                                <span style={{ flex: 1 }}>{u.name}</span>
                                <span style={{ color: '#888', fontSize: '0.74rem' }}>{u.email}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} style={{ padding: '7px 16px', background: 'transparent', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" disabled={busy} onClick={() => void create()} style={{ padding: '7px 16px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>{busy ? '…' : t('Kreiraj tim', 'Create team')}</button>
                </div>
            </div>
        </div>
    );
}

/* ===== ManagePanel — projekti + xlsx-style task table ===== */

function ManagePanel({ teamId, token, locale, onChanged }: { teamId: number; token: string | null; locale: Locale; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [projects, setProjects] = useState<TeamProject[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [activeProjectId, setActiveProjectId] = useState<number | null>(null);
    const [tasks, setTasks] = useState<TeamTask[]>([]);
    const [showNewProject, setShowNewProject] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [showNewTask, setShowNewTask] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        setBusy(true);
        try {
            const [pr, mr] = await Promise.all([
                api<{ projects: TeamProject[] }>(`/tasks/projects?workspace_id=${teamId}`, token),
                api<{ members: Member[] }>(`/tasks/teams/${teamId}/members-list`, token),
            ]);
            setProjects(pr.projects);
            setMembers(mr.members);
            if (!activeProjectId && pr.projects.length > 0) setActiveProjectId(pr.projects[0].id);
            if (activeProjectId && !pr.projects.find((p) => p.id === activeProjectId)) setActiveProjectId(pr.projects[0]?.id ?? null);
        } catch (e) { alert(String(e)); }
        setBusy(false);
    }, [token, teamId, activeProjectId]);

    useEffect(() => { void load(); }, [load]);
    // Reset projectId when switching teams
    useEffect(() => { setActiveProjectId(null); }, [teamId]);

    const loadTasks = useCallback(async (projectId: number) => {
        if (!token) return;
        try {
            const r = await api<{ sections: unknown[]; tasks: TeamTask[] }>(`/tasks/projects/${projectId}/tasks`, token);
            setTasks(r.tasks);
        } catch (e) { alert(String(e)); }
    }, [token]);

    useEffect(() => { if (activeProjectId !== null) void loadTasks(activeProjectId); }, [activeProjectId, loadTasks]);

    const createProject = async () => {
        if (!token) return;
        if (!newProjectName.trim()) return;
        try {
            const r = await mutate<{ ok: boolean; id: number }>('/tasks/projects', token, 'POST', {
                workspace_id: teamId,
                name: newProjectName.trim(),
            });
            setShowNewProject(false); setNewProjectName('');
            setActiveProjectId(r.id);
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    const patchTask = async (taskId: number, patch: Partial<TeamTask>) => {
        if (!token) return;
        try {
            await mutate(`/tasks/tasks/${taskId}`, token, 'PATCH', patch as Record<string, unknown>);
            if (activeProjectId) await loadTasks(activeProjectId);
            onChanged();
        } catch (e) { alert(String(e)); }
    };

    const deleteTask = async (taskId: number) => {
        if (!token) return;
        if (!confirm(t('Obriši task?', 'Delete task?'))) return;
        try {
            await mutate(`/tasks/tasks/${taskId}`, token, 'DELETE');
            if (activeProjectId) await loadTasks(activeProjectId);
            onChanged();
        } catch (e) { alert(String(e)); }
    };

    const activeProject = projects.find((p) => p.id === activeProjectId) ?? null;

    return (
        <div style={{ fontSize: '0.86rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
                <strong style={{ color: BROWN }}>{t('Projekti', 'Projects')}:</strong>
                {projects.map((p) => (
                    <button key={p.id} type="button" onClick={() => setActiveProjectId(p.id)}
                        style={{ padding: '4px 12px', background: activeProjectId === p.id ? GOLD : 'transparent', color: activeProjectId === p.id ? '#fff' : BROWN, border: '1px solid var(--ad-acc)', borderRadius: 14, cursor: 'pointer', fontSize: '0.8rem' }}>
                        {p.icon} {p.name} <span style={{ opacity: 0.7, marginLeft: 4 }}>({p.task_count})</span>
                    </button>
                ))}
                <button type="button" onClick={() => setShowNewProject(true)}
                    style={{ padding: '4px 12px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 14, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>+ {t('Novi projekt', 'New project')}</button>
                {busy ? <span className="muted" style={{ fontSize: '0.74rem' }}>…</span> : null}
            </div>

            {showNewProject ? (
                <div style={{ marginBottom: 12, padding: 10, background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 6 }}>
                    <input type="text" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder={t('Naziv projekta', 'Project name')} autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') void createProject(); if (e.key === 'Escape') setShowNewProject(false); }}
                        style={{ width: '50%', padding: '6px 10px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.86rem' }} />
                    <button type="button" onClick={() => void createProject()} style={{ marginLeft: 8, padding: '6px 14px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>{t('Kreiraj', 'Create')}</button>
                    <button type="button" onClick={() => setShowNewProject(false)} style={{ marginLeft: 4, padding: '6px 12px', background: 'transparent', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem' }}>{t('Odustani', 'Cancel')}</button>
                </div>
            ) : null}

            {activeProject ? (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <strong style={{ color: BROWN, fontSize: '1rem' }}>{activeProject.icon} {activeProject.name}</strong>
                        <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#888' }}>{tasks.length} {t('zadataka', 'tasks')}</span>
                        <button type="button" onClick={() => setShowNewTask(true)} style={{ padding: '4px 14px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>+ {t('Novi zadatak', 'New task')}</button>
                    </div>

                    {showNewTask ? (
                        <NewTaskRow projectId={activeProject.id} members={members} token={token} locale={locale}
                            onCreated={() => { setShowNewTask(false); if (activeProjectId) void loadTasks(activeProjectId); onChanged(); }}
                            onCancel={() => setShowNewTask(false)} />
                    ) : null}

                    <div style={{ background: '#fff', borderRadius: 6, overflowX: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.2)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                                <tr style={{ background: 'var(--ad-bg)', color: BROWN, fontSize: '0.74rem' }}>
                                    <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>MILESTONES</th>
                                    <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee', width: 150 }}>ASSIGNED</th>
                                    <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee', width: 130 }}>STATUS</th>
                                    <th style={{ padding: '8px 4px', textAlign: 'center', borderBottom: '1px solid #eee', width: 90 }}>START</th>
                                    <th style={{ padding: '8px 4px', textAlign: 'center', borderBottom: '1px solid #eee', width: 90 }}>END</th>
                                    <th style={{ padding: '8px 4px', textAlign: 'right', borderBottom: '1px solid #eee', width: 60 }}>Done</th>
                                    <th style={{ padding: '8px 4px', textAlign: 'right', borderBottom: '1px solid #eee', width: 60 }}>Plan</th>
                                    <th style={{ padding: '8px 4px', textAlign: 'right', borderBottom: '1px solid #eee', width: 40 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {tasks.length === 0 ? (
                                    <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#888' }}>{t('Nema zadataka. Klikni "+ Novi zadatak".', 'No tasks. Click "+ New task".')}</td></tr>
                                ) : null}
                                {tasks.map((task) => (
                                    <TaskRow key={task.id} task={task} members={members} locale={locale}
                                        onPatch={(p) => void patchTask(task.id, p)}
                                        onDelete={() => void deleteTask(task.id)} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                !showNewProject ? <p className="muted" style={{ fontSize: '0.82rem' }}>{t('Kreiraj prvi projekt tima.', 'Create the first team project.')}</p> : null
            )}
        </div>
    );
}

function NewTaskRow({ projectId, members, token, locale, onCreated, onCancel }: { projectId: number; members: Member[]; token: string | null; locale: Locale; onCreated: () => void; onCancel: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [title, setTitle] = useState('');
    const [assigneeId, setAssigneeId] = useState<number | ''>('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [plan, setPlan] = useState('');

    const create = async () => {
        if (!token) return;
        if (!title.trim()) { alert(t('Naslov je obavezan', 'Title is required')); return; }
        try {
            await mutate('/tasks/tasks', token, 'POST', {
                project_id: projectId,
                title: title.trim(),
                assignee_user_id: assigneeId || null,
                start_date: start || null,
                due_date: end || null,
                progress_plan: plan ? Number(plan) : null,
            });
            onCreated();
        } catch (e) { alert(String(e)); }
    };

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: 10, background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', borderRadius: 6, marginBottom: 8 }}>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('Naslov zadatka', 'Task title')} autoFocus
                style={{ flex: '1 1 240px', padding: '6px 10px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.86rem' }} />
            <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value ? Number(e.target.value) : '')}
                style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.82rem' }}>
                <option value="">{t('Bez dodjele', 'No assignee')}</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <div style={{ width: 150 }}>
                <DateInput value={start} onChange={setStart}
                    style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.82rem' }} />
            </div>
            <div style={{ width: 150 }}>
                <DateInput value={end} onChange={setEnd}
                    style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.82rem' }} />
            </div>
            <input type="number" min={0} value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="Plan"
                style={{ width: 80, padding: '6px 8px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.82rem' }} />
            <button type="button" onClick={() => void create()} style={{ padding: '6px 14px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>+ {t('Kreiraj', 'Create')}</button>
            <button type="button" onClick={onCancel} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
        </div>
    );
}

function TaskRow({ task, members, locale, onPatch, onDelete }: { task: TeamTask; members: Member[]; locale: Locale; onPatch: (p: Partial<TeamTask>) => void; onDelete: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <tr style={{ borderBottom: '1px solid #f6f6f6' }}>
            <td style={{ padding: '5px 10px' }}>
                <input type="text" defaultValue={task.title}
                    onBlur={(e) => { if (e.target.value.trim() !== task.title) onPatch({ title: e.target.value.trim() }); }}
                    style={{ width: '100%', border: 0, background: 'transparent', fontSize: '0.84rem', color: '#444', padding: 4 }} />
            </td>
            <td style={{ padding: '5px 8px' }}>
                <select value={task.assignee_user_id ?? ''} onChange={(e) => onPatch({ assignee_user_id: e.target.value ? Number(e.target.value) : null })}
                    style={{ width: '100%', border: 0, background: 'transparent', fontSize: '0.76rem', color: '#666' }}>
                    <option value="">{t('—', '—')}</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
            </td>
            <td style={{ padding: '5px 8px' }}>
                <select value={task.custom_status ?? ''} onChange={(e) => onPatch({ custom_status: e.target.value || null })}
                    style={{ width: '100%', border: 0, background: 'transparent', fontSize: '0.72rem', color: statusTextColor(task.custom_status), fontWeight: 700 }}>
                    <option value="">—</option>
                    <option value="not_started">NOT STARTED</option>
                    <option value="in_progress">IN PROGRESS</option>
                    <option value="on_hold">ON HOLD</option>
                    <option value="completed">COMPLETED</option>
                </select>
            </td>
            <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                <DateCell iso={task.start_date} onChange={(iso) => onPatch({ start_date: iso })} />
            </td>
            <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                <DateCell iso={task.due_date} onChange={(iso) => onPatch({ due_date: iso })} />
            </td>
            <td style={{ padding: '5px 4px', textAlign: 'right' }}>
                <input type="number" min={0} defaultValue={task.progress_done ?? ''}
                    onBlur={(e) => { const v = e.target.value ? Number(e.target.value) : null; if (v !== task.progress_done) onPatch({ progress_done: v }); }}
                    style={{ width: 48, border: 0, background: 'transparent', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }} />
            </td>
            <td style={{ padding: '5px 4px', textAlign: 'right' }}>
                <input type="number" min={0} defaultValue={task.progress_plan ?? ''}
                    onBlur={(e) => { const v = e.target.value ? Number(e.target.value) : null; if (v !== task.progress_plan) onPatch({ progress_plan: v }); }}
                    style={{ width: 48, border: 0, background: 'transparent', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }} />
            </td>
            <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                <button type="button" onClick={onDelete} title={t('Obriši', 'Delete')}
                    style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#b14040', fontSize: '0.9rem' }}>✕</button>
            </td>
        </tr>
    );
}

function DateCell({ iso, onChange, width = 80 }: { iso: string | null; onChange: (iso: string | null) => void; width?: number }) {
    const [val, setVal] = useState(isoToDDMM(iso));
    const dateRef = useRef<HTMLInputElement>(null);
    useEffect(() => { setVal(isoToDDMM(iso)); }, [iso]);

    const openPicker = () => {
        const el = dateRef.current;
        if (!el) return;
        try {
            // showPicker je Chrome 99+ / Safari 16+ — daje native kalendar bez fokusiranja
            if (typeof (el as HTMLInputElement & { showPicker?: () => void }).showPicker === 'function') {
                (el as HTMLInputElement & { showPicker: () => void }).showPicker();
            } else {
                el.focus(); el.click();
            }
        } catch { el.focus(); }
    };

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <input type="text" value={val} onChange={(e) => setVal(e.target.value)}
                onBlur={() => { const parsed = parseDateInput(val); if (parsed !== iso) onChange(parsed); }}
                placeholder="dd.mm.yyyy"
                style={{ width, border: 0, background: 'transparent', textAlign: 'center', fontFamily: 'monospace', fontSize: '0.76rem' }} />
            <button type="button" onClick={openPicker} title="📅 kalendar"
                style={{ background: 'transparent', border: 0, cursor: 'pointer', fontSize: '0.84rem', padding: 0, opacity: 0.6 }}>📅</button>
            <input ref={dateRef} type="date" value={iso ?? ''}
                onChange={(e) => onChange(e.target.value || null)}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                tabIndex={-1} />
        </span>
    );
}

function isoToDDMM(iso: string | null): string {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}.` : '';
}

function parseDateInput(s: string): string | null {
    const x = s.trim();
    if (!x) return null;
    let m = x.match(/^(\d{1,2})[.\-/ ](\d{1,2})[.\-/ ](\d{2,4})\.?$/);
    if (m) {
        let yr = m[3];
        if (yr.length === 2) yr = '20' + yr;
        return `${yr}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    }
    m = x.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    return null;
}

function statusTextColor(s: string | null): string {
    if (s === 'completed') return '#1f7a36';
    if (s === 'in_progress') return 'var(--ad-acc)';
    if (s === 'on_hold') return '#8a8a8a';
    if (s === 'not_started') return '#999';
    return '#444';
}

/* ===== GanttChart — xlsx forma lijevo + SVG timeline desno ===== */

function GanttChart({ data, members, locale, onPatch, onOpenTask }: { data: GanttResp; members: Member[]; locale: Locale; onPatch: (taskId: number, p: Partial<TeamTask>) => void; onOpenTask: (id: number) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [zoom, setZoom] = useState<'week' | 'month'>('month');

    const minDate = new Date(data.range.min);
    const maxDate = new Date(data.range.max);
    minDate.setDate(minDate.getDate() - 14);
    maxDate.setDate(maxDate.getDate() + 14);
    const totalDays = Math.max(1, Math.ceil((maxDate.getTime() - minDate.getTime()) / 86400000));
    const dayWidth = zoom === 'week' ? 26 : 6;
    const timelineWidth = totalDays * dayWidth;
    const rowHeight = 26;
    const colW = { id: 44, title: 320, assigned: 120, status: 100, start: 80, end: 80, done: 55, plan: 55, pct: 55 };
    const labelWidth = colW.id + colW.title + colW.assigned + colW.status + colW.start + colW.end + colW.done + colW.plan + colW.pct;
    const headerH = 36;
    const totalH = headerH + data.rows.length * rowHeight + 12;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayX = ((today.getTime() - minDate.getTime()) / 86400000) * dayWidth;

    const monthHeaders: Array<{ label: string; x: number; w: number }> = [];
    const weekHeaders: Array<{ label: string; x: number }> = [];
    {
        const d = new Date(minDate); d.setDate(1);
        while (d <= maxDate) {
            const start = new Date(Math.max(d.getTime(), minDate.getTime()));
            const next = new Date(d); next.setMonth(d.getMonth() + 1);
            const end = new Date(Math.min(next.getTime() - 1, maxDate.getTime()));
            const xs = ((start.getTime() - minDate.getTime()) / 86400000) * dayWidth;
            const xe = ((end.getTime() - minDate.getTime()) / 86400000) * dayWidth;
            const label = d.toLocaleDateString(locale === 'en' ? 'en-GB' : 'hr-HR', { month: 'short', year: '2-digit' });
            monthHeaders.push({ label, x: xs, w: Math.max(20, xe - xs) });
            d.setMonth(d.getMonth() + 1);
        }
    }
    if (zoom === 'week') {
        const d = new Date(minDate);
        const day = d.getDay(); const diff = (day + 6) % 7; d.setDate(d.getDate() - diff);
        while (d <= maxDate) {
            const x = ((d.getTime() - minDate.getTime()) / 86400000) * dayWidth;
            weekHeaders.push({ label: 'W' + getISOWeek(d), x });
            d.setDate(d.getDate() + 7);
        }
    }

    const statusColor = (status?: string, percent?: number): string => {
        if (status === 'completed' || (percent ?? 0) >= 100) return '#1f7a36';
        if (status === 'on_hold') return '#8a8a8a';
        if (status === 'in_progress') return 'var(--ad-acc)';
        if (status === 'not_started') return '#b3b3b3';
        return 'var(--ad-ink)';
    };

    const renderBar = (row: GanttRow, y: number) => {
        if (!row.start_date || !row.end_date) return null;
        const s = new Date(row.start_date);
        const e = new Date(row.end_date);
        const x = ((s.getTime() - minDate.getTime()) / 86400000) * dayWidth;
        const w = Math.max(dayWidth, ((e.getTime() - s.getTime()) / 86400000 + 1) * dayWidth);
        const c = statusColor(row.status, row.percent);
        const fillW = w * Math.min(100, row.percent ?? 0) / 100;
        if (row.kind === 'project') {
            return (
                <g key={row.id}>
                    <rect x={x} y={y + 7} width={w} height={4} fill={BROWN} />
                    <polygon points={`${x},${y + 5} ${x + 6},${y + 15} ${x - 6},${y + 15}`} fill={BROWN} />
                    <polygon points={`${x + w},${y + 5} ${x + w + 6},${y + 15} ${x + w - 6},${y + 15}`} fill={BROWN} />
                </g>
            );
        }
        return (
            <g key={row.id}>
                <rect x={x} y={y + 5} width={w} height={16} fill="#fff" stroke={c} strokeWidth={1} rx={3} />
                <rect x={x} y={y + 5} width={fillW} height={16} fill={c} rx={3} opacity={0.75} />
                {(row.percent ?? 0) > 0 ? (
                    <text x={x + Math.max(6, fillW / 2)} y={y + 16} fontSize={9} fill="#fff" textAnchor="middle" fontWeight={700}>{row.percent}%</text>
                ) : null}
            </g>
        );
    };

    const fmtD = (s: string | null) => {
        if (!s) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
        return m ? `${m[3]}.${m[2]}.` : '';
    };
    const statusLabel = (st?: string) => {
        if (st === 'completed') return 'COMPLETED';
        if (st === 'in_progress') return 'IN PROGRESS';
        if (st === 'on_hold') return 'ON HOLD';
        if (st === 'not_started') return 'NOT STARTED';
        return st ?? '';
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: '0.78rem', color: '#888' }}>
                    {data.range.min} → {data.range.max} · {data.rows.length} {t('redaka', 'rows')}
                </span>
                <button type="button" onClick={() => setZoom('month')} style={{ marginLeft: 'auto', padding: '4px 12px', background: zoom === 'month' ? GOLD : 'transparent', color: zoom === 'month' ? '#fff' : BROWN, border: '1px solid var(--ad-acc)', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer' }}>{t('Mjesec', 'Month')}</button>
                <button type="button" onClick={() => setZoom('week')} style={{ padding: '4px 12px', background: zoom === 'week' ? GOLD : 'transparent', color: zoom === 'week' ? '#fff' : BROWN, border: '1px solid var(--ad-acc)', borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer' }}>{t('Tjedan', 'Week')}</button>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, background: '#fff' }}>
                <div style={{ display: 'flex', minWidth: labelWidth + timelineWidth }}>
                    <div style={{ width: labelWidth, borderRight: '1px solid #eee', flexShrink: 0, fontSize: '0.76rem', color: '#444' }}>
                        <div style={{ display: 'flex', height: headerH, borderBottom: '1px solid #ddd', background: 'var(--ad-bg)', color: BROWN, fontWeight: 700, fontSize: '0.72rem' }}>
                            <div style={{ width: colW.id, padding: '10px 4px', borderRight: '1px solid #eee', textAlign: 'center' }}>ID</div>
                            <div style={{ width: colW.title, padding: '10px 6px', borderRight: '1px solid #eee' }}>MILESTONES</div>
                            <div style={{ width: colW.assigned, padding: '10px 6px', borderRight: '1px solid #eee' }}>ASSIGNED</div>
                            <div style={{ width: colW.status, padding: '10px 6px', borderRight: '1px solid #eee' }}>STATUS</div>
                            <div style={{ width: colW.start, padding: '10px 4px', borderRight: '1px solid #eee', textAlign: 'center' }}>START</div>
                            <div style={{ width: colW.end, padding: '10px 4px', borderRight: '1px solid #eee', textAlign: 'center' }}>END</div>
                            <div style={{ width: colW.done, padding: '10px 4px', borderRight: '1px solid #eee', textAlign: 'right' }}>Done</div>
                            <div style={{ width: colW.plan, padding: '10px 4px', borderRight: '1px solid #eee', textAlign: 'right' }}>Plan</div>
                            <div style={{ width: colW.pct, padding: '10px 4px', textAlign: 'right' }}>% DONE</div>
                        </div>
                        {data.rows.map((row, i) => {
                            const isProj = row.kind === 'project';
                            const indent = isProj ? 0 : (row.level - 1) * 10;
                            const taskId = row.task_id ?? 0;
                            return (
                                <div key={row.id} style={{
                                    display: 'flex', height: rowHeight,
                                    borderBottom: '1px solid #f6f6f6',
                                    background: isProj ? 'rgba(var(--ad-acc-rgb),0.12)' : (i % 2 === 0 ? '#fff' : '#fafafa'),
                                    fontWeight: isProj ? 700 : 500,
                                    color: isProj ? BROWN : '#444',
                                }}>
                                    <div style={{ width: colW.id, padding: '5px 4px', borderRight: '1px solid #eee', textAlign: 'center', fontFamily: 'monospace' }}>{isProj ? '' : (row.id_label ?? '')}</div>
                                    {isProj ? (
                                        <div style={{ width: colW.title, padding: `5px 6px 5px ${6 + indent}px`, borderRight: '1px solid #eee', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.25, fontWeight: 600 }} title={row.title}>
                                            ▸ {row.title}
                                        </div>
                                    ) : (
                                        <div style={{ width: colW.title, paddingLeft: 6 + indent, borderRight: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <input type="text" defaultValue={row.title}
                                                onBlur={(e) => { if (taskId && e.target.value.trim() !== row.title) onPatch(taskId, { title: e.target.value.trim() }); }}
                                                style={{ flex: 1, border: 0, background: 'transparent', fontSize: '0.78rem', color: '#444', padding: '5px 4px' }}
                                                title={row.title} />
                                            {taskId ? (
                                                <button type="button" onClick={() => onOpenTask(taskId)} title={t('Otvori detalje', 'Open details')}
                                                    style={{ background: 'transparent', border: '1px solid var(--ad-acc)', borderRadius: 4, padding: '1px 5px', cursor: 'pointer', fontSize: '0.66rem', color: BROWN, marginRight: 4, whiteSpace: 'nowrap' }}>
                                                    💬{(row.comment_count ?? 0) > 0 ? row.comment_count : ''}{(row.nudge_count ?? 0) > 0 ? ` 🫵${row.nudge_count}` : ''}
                                                </button>
                                            ) : null}
                                        </div>
                                    )}
                                    {isProj ? (
                                        <div style={{ width: colW.assigned, padding: '5px 6px', borderRight: '1px solid #eee' }} />
                                    ) : (
                                        <select value={taskId && row.assignee_user_name ? (members.find((m) => m.name === row.assignee_user_name)?.id ?? '') : ''}
                                            onChange={(e) => taskId && onPatch(taskId, { assignee_user_id: e.target.value ? Number(e.target.value) : null })}
                                            style={{ width: colW.assigned, padding: '5px 4px', borderRight: '1px solid #eee', border: 0, background: 'transparent', fontSize: '0.72rem', color: '#666' }}>
                                            <option value="">—</option>
                                            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                                        </select>
                                    )}
                                    {isProj ? (
                                        <div style={{ width: colW.status, padding: '5px 6px', borderRight: '1px solid #eee' }} />
                                    ) : (
                                        <select value={row.status ?? ''}
                                            onChange={(e) => taskId && onPatch(taskId, { custom_status: e.target.value || null })}
                                            style={{ width: colW.status, padding: '5px 4px', borderRight: '1px solid #eee', border: 0, background: 'transparent', fontSize: '0.7rem', color: statusTextColor(row.status ?? null), fontWeight: 700 }}>
                                            <option value="">—</option>
                                            <option value="not_started">NOT STARTED</option>
                                            <option value="in_progress">IN PROGRESS</option>
                                            <option value="on_hold">ON HOLD</option>
                                            <option value="completed">COMPLETED</option>
                                        </select>
                                    )}
                                    {isProj ? (
                                        <div style={{ width: colW.start, padding: '5px 4px', borderRight: '1px solid #eee', textAlign: 'center', fontFamily: 'monospace', fontSize: '0.72rem' }}>{fmtD(row.start_date)}</div>
                                    ) : (
                                        <div style={{ width: colW.start, padding: '3px 2px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                                            <DateCell iso={row.start_date} onChange={(iso) => taskId && onPatch(taskId, { start_date: iso })} />
                                        </div>
                                    )}
                                    {isProj ? (
                                        <div style={{ width: colW.end, padding: '5px 4px', borderRight: '1px solid #eee', textAlign: 'center', fontFamily: 'monospace', fontSize: '0.72rem' }}>{fmtD(row.end_date)}</div>
                                    ) : (
                                        <div style={{ width: colW.end, padding: '3px 2px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                                            <DateCell iso={row.end_date} onChange={(iso) => taskId && onPatch(taskId, { due_date: iso })} />
                                        </div>
                                    )}
                                    {isProj ? (
                                        <div style={{ width: colW.done, padding: '5px 4px', borderRight: '1px solid #eee' }} />
                                    ) : (
                                        <input type="number" min={0} defaultValue={row.progress_done ?? ''}
                                            onBlur={(e) => { const v = e.target.value ? Number(e.target.value) : null; if (taskId && v !== (row.progress_done ?? null)) onPatch(taskId, { progress_done: v }); }}
                                            style={{ width: colW.done, padding: '5px 4px', borderRight: '1px solid #eee', border: 0, background: 'transparent', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }} />
                                    )}
                                    {isProj ? (
                                        <div style={{ width: colW.plan, padding: '5px 4px', borderRight: '1px solid #eee' }} />
                                    ) : (
                                        <input type="number" min={0} defaultValue={row.progress_plan ?? ''}
                                            onBlur={(e) => { const v = e.target.value ? Number(e.target.value) : null; if (taskId && v !== (row.progress_plan ?? null)) onPatch(taskId, { progress_plan: v }); }}
                                            style={{ width: colW.plan, padding: '5px 4px', borderRight: '1px solid #eee', border: 0, background: 'transparent', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }} />
                                    )}
                                    <div style={{ width: colW.pct, padding: '5px 4px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: (row.percent ?? 0) >= 100 ? '#1f7a36' : (row.percent ?? 0) > 0 ? 'var(--ad-acc)' : '#999' }}>
                                        {(row.percent ?? 0) > 0 ? `${row.percent}%` : ''}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ position: 'relative' }}>
                        <svg width={timelineWidth} height={totalH} style={{ display: 'block' }}>
                            <g>
                                {monthHeaders.map((m, i) => (
                                    <g key={i}>
                                        <rect x={m.x} y={0} width={m.w} height={20} fill={i % 2 === 0 ? 'var(--ad-bg)' : '#fff'} stroke="#eee" />
                                        <text x={m.x + 6} y={14} fontSize={11} fill={BROWN} fontWeight={700}>{m.label}</text>
                                    </g>
                                ))}
                                {weekHeaders.map((w, i) => (
                                    <g key={i}>
                                        <line x1={w.x} x2={w.x} y1={20} y2={headerH} stroke="#ddd" />
                                        <text x={w.x + 2} y={32} fontSize={9} fill="#888">{w.label}</text>
                                    </g>
                                ))}
                            </g>
                            {todayX >= 0 && todayX <= timelineWidth ? (
                                <line x1={todayX} x2={todayX} y1={0} y2={totalH} stroke="#b14040" strokeWidth={1.5} strokeDasharray="3,3" />
                            ) : null}
                            {data.rows.map((row, i) => {
                                const y = headerH + i * rowHeight;
                                return (
                                    <g key={row.id}>
                                        <rect x={0} y={y} width={timelineWidth} height={rowHeight}
                                            fill={row.kind === 'project' ? 'rgba(var(--ad-acc-rgb),0.08)' : (i % 2 === 0 ? '#fff' : '#fafafa')} />
                                        {renderBar(row, y)}
                                    </g>
                                );
                            })}
                        </svg>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#888' }}>
                {t('Legenda', 'Legend')}:
                <span style={{ marginLeft: 10 }}><span style={{ color: '#1f7a36' }}>■ {t('Završeno', 'Completed')}</span></span>
                <span style={{ marginLeft: 10 }}><span style={{ color: 'var(--ad-acc)' }}>■ {t('U tijeku', 'In progress')}</span></span>
                <span style={{ marginLeft: 10 }}><span style={{ color: '#b3b3b3' }}>■ {t('Nije počelo', 'Not started')}</span></span>
                <span style={{ marginLeft: 10 }}><span style={{ color: '#8a8a8a' }}>■ {t('Na čekanju', 'On hold')}</span></span>
                <span style={{ marginLeft: 10, color: '#b14040' }}>┊ {t('Danas', 'Today')}</span>
            </div>
        </div>
    );
}

function getISOWeek(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/* ===== v344 TaskDrawer — task detalji + komentari + nudge history + dodaj komentar ===== */

function TaskDrawer({ taskId, token, locale, onClose, onChanged }: { taskId: number; token: string | null; locale: Locale; onClose: () => void; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [detail, setDetail] = useState<TaskDetailResp | null>(null);
    const [nudges, setNudges] = useState<TaskNudge[]>([]);
    const [busy, setBusy] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        if (!token) return;
        setBusy(true);
        try {
            const [d, n] = await Promise.all([
                api<TaskDetailResp>(`/tasks/tasks/${taskId}`, token),
                api<{ nudges: TaskNudge[] }>(`/tasks/tasks/${taskId}/nudges`, token).catch(() => ({ nudges: [] })),
            ]);
            setDetail(d); setNudges(n.nudges);
        } catch (e) { /* */ }
        setBusy(false);
    }, [token, taskId]);

    useEffect(() => { void load(); }, [load]);
    useAutoRefresh(load, 30000);

    const addComment = async () => {
        if (!token || !newComment.trim()) return;
        setSending(true);
        try {
            await mutate(`/tasks/tasks/${taskId}/comments`, token, 'POST', { body: newComment.trim() });
            setNewComment('');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
        setSending(false);
    };

    const toggleComplete = async () => {
        if (!token) return;
        try {
            await mutate(`/tasks/tasks/${taskId}/complete`, token, 'POST');
            await load(); onChanged();
        } catch (e) { alert(String(e)); }
    };

    const fmtDateTime = (s: string | null) => {
        if (!s) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
        return m ? `${m[3]}.${m[2]}.${m[1]}. ${m[4]}:${m[5]}` : s;
    };
    const sourceLabel = (s: string) => {
        if (s === 'email_nudge_reply') return '📧 ' + t('email reply', 'email reply');
        if (s === 'email_intake') return '📥 ' + t('email intake', 'email intake');
        return '✏️ ' + t('web', 'web');
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 80, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(680px, 96vw)', background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-6px 0 20px rgba(0,0,0,0.15)' }}>
                {/* Sticky header */}
                <div style={{ position: 'sticky', top: 0, background: 'var(--ad-bg)', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.3)', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, zIndex: 1 }}>
                    <strong style={{ color: BROWN, fontSize: '1.05rem' }}>📋 {t('Task', 'Task')} #{taskId}</strong>
                    {detail?.task.completed_at ? <span style={{ background: '#1f7a36', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 800 }}>✓ {t('ZAVRŠENO', 'COMPLETED')}</span> : null}
                    <button type="button" onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid #ccc', borderRadius: 5, padding: '4px 10px', cursor: 'pointer' }}>✕</button>
                </div>

                <div style={{ padding: 18 }}>
                    {busy && !detail ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

                    {detail ? (
                        <>
                            <h3 style={{ marginTop: 0, color: BROWN, fontSize: '1.15rem', textDecoration: detail.task.completed_at ? 'line-through' : 'none' }}>{detail.task.title}</h3>

                            {/* Meta grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14, fontSize: '0.82rem' }}>
                                <div style={{ padding: 8, background: '#fafafa', borderRadius: 5 }}>
                                    <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>{t('Nositelj', 'Assignee')}</div>
                                    <div style={{ fontWeight: 700, color: detail.task.assignee_name ? BROWN : '#aaa' }}>{detail.task.assignee_name ?? t('— bez —', '— none —')}</div>
                                </div>
                                <div style={{ padding: 8, background: '#fafafa', borderRadius: 5 }}>
                                    <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>{t('Status', 'Status')}</div>
                                    <div style={{ fontWeight: 700, color: statusTextColor(detail.task.custom_status) }}>{detail.task.custom_status ?? '—'}</div>
                                </div>
                                <div style={{ padding: 8, background: '#fafafa', borderRadius: 5 }}>
                                    <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Start</div>
                                    <div style={{ fontFamily: 'monospace' }}>{detail.task.start_date ?? '—'}</div>
                                </div>
                                <div style={{ padding: 8, background: '#fafafa', borderRadius: 5 }}>
                                    <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Due</div>
                                    <div style={{ fontFamily: 'monospace' }}>{detail.task.due_date ?? '—'}</div>
                                </div>
                                <div style={{ padding: 8, background: '#fafafa', borderRadius: 5 }}>
                                    <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>{t('Progress', 'Progress')}</div>
                                    <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                        {detail.task.progress_done ?? 0} / {detail.task.progress_plan ?? 0}
                                        {detail.task.progress_plan && detail.task.progress_plan > 0
                                            ? <span style={{ marginLeft: 6, color: GOLD }}>({Math.round(((detail.task.progress_done ?? 0) / detail.task.progress_plan) * 100)}%)</span>
                                            : null}
                                    </div>
                                </div>
                                <div style={{ padding: 8, background: '#fafafa', borderRadius: 5 }}>
                                    <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>{t('Prioritet', 'Priority')}</div>
                                    <div style={{ fontWeight: 700, color: detail.task.priority === 'urgent' ? '#b14040' : '#444' }}>{detail.task.priority}</div>
                                </div>
                            </div>

                            <button type="button" onClick={() => void toggleComplete()}
                                style={{ padding: '6px 14px', background: detail.task.completed_at ? 'var(--ad-bg)' : '#1f7a36', color: detail.task.completed_at ? BROWN : '#fff', border: detail.task.completed_at ? '1px solid var(--ad-acc)' : 0, borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.86rem', marginBottom: 12 }}>
                                {detail.task.completed_at ? '↶ ' + t('Vrati u otvorene', 'Reopen') : '✓ ' + t('Završi', 'Complete')}
                            </button>

                            {detail.task.description ? (
                                <div style={{ marginBottom: 14, padding: 10, background: 'var(--ad-bg)', borderRadius: 5, fontSize: '0.86rem', whiteSpace: 'pre-wrap', color: '#444' }}>
                                    {detail.task.description}
                                </div>
                            ) : null}

                            {/* Nudges */}
                            {nudges.length > 0 ? (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>🫵 {t('Nudges', 'Nudges')} ({nudges.length})</div>
                                    {nudges.map((n) => (
                                        <div key={n.id} style={{ marginBottom: 6, padding: 8, background: 'var(--ad-bg)', borderRadius: 5, borderLeft: `3px solid ${n.replied_at ? '#1f7a36' : GOLD}`, fontSize: '0.8rem' }}>
                                            <div><strong>{n.nudger_name}</strong> → {n.nudgee_name} <span style={{ marginLeft: 6, color: '#888', fontSize: '0.72rem' }}>{fmtDateTime(n.created_at)}</span></div>
                                            {n.note ? <div style={{ marginTop: 3, fontStyle: 'italic', color: '#555' }}>„{n.note}"</div> : null}
                                            <div style={{ marginTop: 3, fontSize: '0.72rem', color: '#888' }}>
                                                {n.replied_at ? '✓ ' + t('Odgovorio', 'Replied') + ' ' + fmtDateTime(n.replied_at) : '⏳ ' + t('Čeka odgovor', 'Awaiting reply')}
                                                · email: <span style={{ color: n.email_status === 'sent' ? '#1f7a36' : '#b14040' }}>{n.email_status}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {/* Comments */}
                            <div style={{ marginBottom: 14 }}>
                                <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>💬 {t('Komentari', 'Comments')} ({detail.comments.length})</div>
                                {detail.comments.length === 0 ? <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Još nema komentara.', 'No comments yet.')}</p> : null}
                                {detail.comments.map((c) => (
                                    <div key={c.id} style={{ marginBottom: 8, padding: 10, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.2)', borderRadius: 5 }}>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, fontSize: '0.78rem' }}>
                                            <strong style={{ color: BROWN }}>{c.author_name ?? c.author_email ?? '?'}</strong>
                                            <span style={{ color: '#888' }}>{sourceLabel(c.source)}</span>
                                            <span style={{ marginLeft: 'auto', color: '#888', fontSize: '0.72rem' }}>{fmtDateTime(c.created_at)}</span>
                                        </div>
                                        <div style={{ fontSize: '0.84rem', whiteSpace: 'pre-wrap', color: '#444' }}>{c.body}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Add comment */}
                            <div style={{ padding: 10, background: 'var(--ad-bg)', borderRadius: 6 }}>
                                <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={3}
                                    placeholder={t('Dodaj komentar…', 'Add a comment…')}
                                    style={{ width: '100%', padding: '6px 10px', borderRadius: 5, border: '1px solid var(--ad-acc)', fontSize: '0.84rem', resize: 'vertical', fontFamily: 'inherit' }} />
                                <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                                    <button type="button" disabled={sending || !newComment.trim()} onClick={() => void addComment()}
                                        style={{ padding: '5px 14px', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 5, cursor: sending ? 'wait' : 'pointer', fontWeight: 700, fontSize: '0.82rem' }}>
                                        {sending ? '…' : t('Pošalji', 'Send')}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

// v347.6 — mobile detect za master-detail layout (sidebar ↔ detail)
function useIsMobile(threshold = 720): boolean {
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < threshold);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < threshold);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [threshold]);
    return isMobile;
}
